import { convexQuery } from "@convex-dev/react-query";
import type { Id } from "@convex/_generated/dataModel";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";

import {
  categoriesGetAll,
  itemsGetAll,
  modifiersApplyModifierGroup,
  modifiersCreateGroup,
  modifiersGetAllGroups,
} from "~/api-refs";
import { ApplyNewModifierForm } from "~/components/menus/modifier-modal/apply-new-modifier-form";
import type { ModalType } from "~/components/menus/modifier-modal/common";
import { ModalTypeForm } from "~/components/menus/modifier-modal/modal-type-form";
import { ModifierForm } from "~/components/menus/modifier-modal/modifier-form";
import { LoadingPage } from "~/components/shared/loading-page";
import { Button } from "~/components/ui/button";
import { Combobox } from "~/components/ui/combobox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Label } from "~/components/ui/label";
import { MultiCombobox } from "~/components/ui/multi-combobox";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";
import { logger } from "~/lib/logger";
import { parseEnumValue } from "~/lib/parse-enum-value";
import { ProductEvents } from "~/lib/posthog";
import type { ModifierFormSchema } from "~/lib/validators/modifiers";
import { isModifierType } from "~/lib/validators/modifiers";

export const Route = createFileRoute(
  "/_authenticated/menus/$menuId/modifiers/new"
)({
  component: CreateModifierModal,
});

// TypeScript type for apply existing modifier form
interface ApplyExistingModifierFormData {
  modifierGroupId: string;
  applyTo: {
    categories: string[];
    items: string[];
  };
}

function CreateModifierModal() {
  const { menuId } = Route.useParams();
  const navigate = useNavigate();
  const [modalType, setModalType] = useState<ModalType | null>(null);

  const handleClose = () => {
    void navigate({ to: `/menus/${menuId}/modifiers` });
  };

  const handleFinished = () => {
    handleClose();
  };

  return (
    <Dialog onOpenChange={handleClose} open>
      <DialogContent className="max-w-176 overflow-x-hidden">
        <ModifierModalContent
          modalType={modalType}
          onFinished={handleFinished}
          setModalType={setModalType}
        />
      </DialogContent>
    </Dialog>
  );
}

const ModifierModalContent = ({
  onFinished,
  modalType,
  setModalType,
}: {
  onFinished: () => void;
  modalType: ModalType | null;
  setModalType: (type: ModalType | null) => void;
}) => {
  if (!modalType) {
    return <ModalTypeForm onSubmit={(itemType) => setModalType(itemType)} />;
  }

  if (modalType === "new") {
    return <AddNewModifier onFinished={onFinished} />;
  }
  if (modalType === "duplicate") {
    return <DuplicateSimilar onFinished={onFinished} />;
  }
  if (modalType === "existing") {
    return <ApplyExistingModifier onFinished={onFinished} />;
  }
  return <p>How on earth did you get here?</p>;
};

const ApplyExistingModifier = ({ onFinished }: { onFinished: () => void }) => {
  const { data: modifierGroupsResult } = useQuery(
    convexQuery(modifiersGetAllGroups, {
      paginationOpts: { numItems: 100, cursor: null },
    })
  );
  const modifierGroups = modifierGroupsResult?.page;
  const { data: itemsResult } = useQuery(
    convexQuery(itemsGetAll, {
      paginationOpts: { numItems: 100, cursor: null },
    })
  );
  const items = itemsResult?.page;
  const { data: categoriesResult } = useQuery(
    convexQuery(categoriesGetAll, {
      paginationOpts: { numItems: 100, cursor: null },
    })
  );
  const categories = categoriesResult?.page;

  const applyModifierMutation = useMutation({
    mutationFn: (data: {
      modifierGroupId: Id<"modifierGroups">;
      applyTo: { categories: Id<"categories">[]; items: Id<"items">[] };
    }) => {
      return convexClient.mutation(modifiersApplyModifierGroup, data);
    },
  });

  const form = useForm<ApplyExistingModifierFormData>({
    defaultValues: {
      modifierGroupId: "",
      applyTo: {
        categories: [],
        items: [],
      },
    },
  });

  const handleSubmit = async (data: ApplyExistingModifierFormData) => {
    if (applyModifierMutation.isPending) {
      return;
    }
    try {
      await applyModifierMutation.mutateAsync({
        modifierGroupId: toConvexTableId(
          "modifierGroups",
          data.modifierGroupId
        ),
        applyTo: {
          categories: data.applyTo.categories.map((c) =>
            toConvexTableId("categories", c)
          ),
          items: data.applyTo.items.map((i) => toConvexTableId("items", i)),
        },
      });
      onFinished();
    } catch (error) {
      logger.exception("modifier_apply_failed", error);
    }
  };

  if (!(modifierGroups && items && categories)) {
    return <LoadingPage />;
  }

  return (
    <Form {...form}>
      <form className="space-y-5" onSubmit={form.handleSubmit(handleSubmit)}>
        <DialogHeader className="items-center space-y-0">
          <DialogTitle>Apply existing modifiers</DialogTitle>
          <DialogDescription>Share and sync modifier groups</DialogDescription>
        </DialogHeader>
        <FormField
          control={form.control}
          name="modifierGroupId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Choose Modifier Group</FormLabel>
              <FormControl>
                <Combobox
                  dropdownClassName="w-130"
                  items={modifierGroups.map((group) => ({
                    label: group.name,
                    value: group._id,
                    keywords: [group.name],
                  }))}
                  onValueChange={(value) => field.onChange(value)}
                  placeholder="Select modifier group..."
                  value={field.value ?? null}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="applyTo"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Apply to Categories or Items</FormLabel>
              <FormControl>
                <MultiCombobox
                  groups={["categories", "items"]}
                  groupLabels={{
                    categories: "Categories",
                    items: "Items",
                  }}
                  items={{
                    categories: categories.map((category) => ({
                      label: category.name,
                      value: category._id,
                    })),
                    items: items.map((item) => ({
                      label: item.name,
                      value: item._id,
                    })),
                  }}
                  onValueChange={(values) => field.onChange(values)}
                  values={field.value ?? { categories: [], items: [] }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button className="w-full" size="lg" type="submit">
          Apply Modifiers
        </Button>
      </form>
    </Form>
  );
};

const DuplicateSimilar = ({
  onFinished,
  selectedDuplicateGroup,
}: {
  onFinished: () => void;
  selectedDuplicateGroup?: ModifierFormSchema;
}) => {
  const [selectedGroup, setSelectedGroup] = useState<ModifierFormSchema | null>(
    selectedDuplicateGroup ?? null
  );

  if (!selectedGroup) {
    return <DuplicateSimilarForm onSubmit={setSelectedGroup} />;
  }

  return <AddNewModifier onFinished={onFinished} values={selectedGroup} />;
};

const DuplicateSimilarForm = ({
  onSubmit,
}: {
  onSubmit: (selectedGroup: ModifierFormSchema) => void;
}) => {
  const { data: modifierGroupsResult } = useQuery(
    convexQuery(modifiersGetAllGroups, {
      paginationOpts: { numItems: 100, cursor: null },
    })
  );
  const modifierGroups = modifierGroupsResult?.page;
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  if (!modifierGroups) {
    return <LoadingPage />;
  }

  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();

        if (!selectedGroupId) {
          return;
        }

        const group = modifierGroups.find((g) => g._id === selectedGroupId);

        if (group) {
          onSubmit({
            id: group._id,
            name: group.name,
            minimum: group.minimum,
            maximum: group.maximum,
            type: "modifier",
            sortOrder: 0,
            parentModifierGroupId: null,
            modifiersList: [
              ...group.modifiers.map((modifier) => ({
                itemId: String(modifier._id),
                name: modifier.name,
                accordionOpen: true,
                sortOrder: modifier.sortOrder,
                modifierType: "modifier" as const,
                data: {
                  additionalCost: modifier.additionalCost.toString(),
                  canBeMultiplied: modifier.canBeMultiplied,
                  isOutOfStock: modifier.isOutOfStock,
                },
              })),
              ...group.subModifierGroups.map((subModifier) => ({
                itemId: String(subModifier._id),
                modifierType: "sub_modifier" as const,
                name: subModifier.name,
                sortOrder: subModifier.sortOrder,
                accordionOpen: true,
                data: {
                  minimum: subModifier.minimum,
                  maximum: subModifier.maximum,
                  type: parseEnumValue(
                    subModifier.type,
                    isModifierType,
                    "sub_modifier"
                  ),
                  parentModifierGroupId: subModifier.parentModifierGroupId
                    ? String(subModifier.parentModifierGroupId)
                    : null,
                  modifiers: subModifier.modifiers.map((modifier) => ({
                    itemId: String(modifier._id),
                    name: modifier.name,
                    sortOrder: modifier.sortOrder,
                    additionalCost: modifier.additionalCost.toString(),
                    isOutOfStock: modifier.isOutOfStock,
                    canBeMultiplied: modifier.canBeMultiplied,
                  })),
                },
              })),
            ],
            modifiers: [],
            subModifierGroups: [],
          });
        }
      }}
    >
      <DialogHeader className="items-center space-y-0">
        <DialogTitle>Duplicate similar modifiers</DialogTitle>
        <DialogDescription>
          Choose an existing modifier group to use as a template
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-2">
        <Label>Choose Modifier Group to Duplicate</Label>
        <Combobox
          dropdownClassName="w-130"
          items={modifierGroups.map((group) => ({
            label: group.name,
            value: group._id,
            keywords: [group.name],
          }))}
          onValueChange={(value) => setSelectedGroupId(value)}
          placeholder="Select modifier group..."
          value={selectedGroupId}
        />
      </div>
      <Button className="w-full" size="lg" type="submit">
        Create Modifier
      </Button>
    </form>
  );
};

const AddNewModifier = ({
  onFinished,
  values,
}: {
  onFinished: () => void;
  values?: ModifierFormSchema;
}) => {
  const [data, setData] = useState<ModifierFormSchema | null>(null);

  const createModifierGroupMutation = useMutation({
    mutationFn: (payload: {
      data: {
        name: string;
        minimum: number;
        maximum: number;
        modifiers: {
          name: string;
          additionalCost: number;
          isOutOfStock: boolean;
          canBeMultiplied: boolean;
          sortOrder: number;
        }[];
        subModifierGroups: {
          name: string;
          maximum: number;
          minimum: number;
          sortOrder: number;
          modifiers: {
            name: string;
            additionalCost: number;
            isOutOfStock: boolean;
            canBeMultiplied: boolean;
          }[];
        }[];
      };
      applyModifierGroupTo: {
        categories: Id<"categories">[];
        items: Id<"items">[];
      };
    }) => {
      return convexClient.mutation(modifiersCreateGroup, payload);
    },
    onSuccess: (result) => {
      if (result?.id) {
        ProductEvents.modifierGroupCreated(result.id);
      }
    },
  });

  const handleSubmit = async (applyTo: {
    categories: string[];
    items: string[];
  }) => {
    if (createModifierGroupMutation.isPending || !data) {
      return;
    }
    try {
      // Transform the data to match backend expectations - only send the required fields
      const modifiers = data.modifiersList
        .filter((modifier) => modifier.modifierType === "modifier")
        .map((modifier) => ({
          name: modifier.name,
          additionalCost: Number(modifier.data.additionalCost),
          isOutOfStock: modifier.data.isOutOfStock,
          canBeMultiplied: modifier.data.canBeMultiplied,
          sortOrder: modifier.sortOrder,
        }));

      const subModifierGroups = data.modifiersList
        .filter((modifier) => modifier.modifierType === "sub_modifier")
        .map((submodifier) => ({
          name: submodifier.name,
          maximum: submodifier.data.maximum,
          minimum: submodifier.data.minimum,
          sortOrder: submodifier.sortOrder,
          modifiers: submodifier.data.modifiers.map((modifier) => ({
            name: modifier.name,
            additionalCost: Number(modifier.additionalCost),
            isOutOfStock: modifier.isOutOfStock,
            canBeMultiplied: modifier.canBeMultiplied,
          })),
        }));

      await createModifierGroupMutation.mutateAsync({
        data: {
          name: data.name,
          minimum: data.minimum,
          maximum: data.maximum,
          modifiers,
          subModifierGroups,
        },
        applyModifierGroupTo: {
          categories: applyTo.categories.map((c) =>
            toConvexTableId("categories", c)
          ),
          items: applyTo.items.map((i) => toConvexTableId("items", i)),
        },
      });
      onFinished();
    } catch (error) {
      logger.exception("modifier_group_create_failed", error);
    }
  };

  if (!data) {
    return (
      <ModifierForm
        editMode={false}
        onSubmit={setData}
        values={values ?? undefined}
      />
    );
  }

  return (
    <ApplyNewModifierForm
      isPending={createModifierGroupMutation.isPending}
      onSubmit={handleSubmit}
    />
  );
};
